import { memo, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, CalendarDays, Clock3 } from 'lucide-react'
import { interludeApi, itemsApi, liveSourcesApi, logoProfilesApi, programsApi } from '../../lib/api.js'
import type { InterludeClip, LiveSource, LiveStartMode, LiveEndMode, LogoProfile, MediaAsset, Program, ScheduleItem } from '../../types/index.js'
import { formatDuration, NO_LOGO_VALUE } from '../../lib/utils.js'
import { ModalShell } from '../common/ModalShell.js'
import {
  broadcastDayKey, broadcastTimeOfDay, instantAtBroadcastClock, todayBroadcastDayKey,
} from '../../lib/broadcast-day.js'
import { getServerNow } from '../../hooks/useServerClock.js'
import { recordGridChange } from '../../lib/gridUndo.js'
import { LivePartPlanEditor } from './LivePartPlanEditor.js'
import { livePartDrafts, type LivePartDraft } from '../../lib/livePartPlan.js'
import { isRiskyLiveStartModeChange, RISKY_LIVE_START_MODE_MESSAGE } from '../../lib/liveStartModeChange.js'
import { mediaAssetIdToSend, type LoadedProgramMedia } from '../../lib/itemMediaChoice.js'
import './AgendaDialogs.css'

/** Jour et heure d'antenne d'un item — ce que la regie lit sur sa conduite. */
function localDateParts(iso: string) {
  const value = new Date(iso)
  return { date: broadcastDayKey(value), time: broadcastTimeOfDay(value) }
}

/** Valeur texte d'une surcharge d'item, `''` si absente ou d'un autre type. */
function overrideText(overrides: Record<string, unknown> | null | undefined, key: string): string {
  const value = overrides?.[key]
  return typeof value === 'string' ? value : ''
}

/** Jour propose par defaut : la journee d'antenne en cours au serveur. */
function todayInputValue() {
  return todayBroadcastDayKey(getServerNow())
}

/** Paramètres éditoriaux d'un direct — surcharges d'abord, fiche liée ensuite. */
function initLiveForm(item: ScheduleItem) {
  const overrides = (item.overrides ?? {}) as Record<string, unknown>
  const readString = (key: string, fallback: string | null | undefined = '') =>
    typeof overrides[key] === 'string' ? String(overrides[key]) : (fallback ?? '')
  const readBool = (key: string, fallback: boolean) =>
    typeof overrides[key] === 'boolean' ? Boolean(overrides[key]) : fallback
  const readNumberText = (key: string, fallback: number | null | undefined) =>
    typeof overrides[key] === 'number' ? String(overrides[key]) : (fallback != null ? String(fallback) : '')
  return {
    title:         readString('title', item.program?.title ?? item.liveSource?.name ?? ''),
    titleShort:    readString('titleShort', item.program?.titleShort ?? ''),
    summary:       readString('summary', item.program?.summary ?? ''),
    genre:         readString('genre', item.program?.genre ?? ''),
    tags:          Array.isArray(overrides.tags)
      ? overrides.tags.filter((tag): tag is string => typeof tag === 'string').join(', ')
      : (item.program?.tags ?? []).join(', '),
    director:      readString('director', item.program?.director ?? ''),
    presenter:     readString('presenter', item.program?.presenter ?? ''),
    production:    readString('production', item.program?.production ?? ''),
    year:          typeof overrides.year === 'number' ? String(overrides.year) : (item.program?.year ? String(item.program.year) : ''),
    logoProfileId: typeof overrides.logoProfileId === 'string' ? overrides.logoProfileId : (item.program?.logoProfileId ?? ''),
    isNew:         readBool('isNew', item.program?.isNew ?? true),
    epgSubtitle:   readString('epgSubtitle'),
    epgFederation: readString('epgFederation', item.program?.epgFederation ?? ''),
    epgThemeId:    readNumberText('epgThemeId', item.program?.epgThemeId),
    epgShowId:     readNumberText('epgShowId', item.program?.epgShowId),
  }
}

export const ScheduleItemEditDialog = memo(function ScheduleItemEditDialog(props: {
  item: ScheduleItem
  channelId: string
  onSaved: (item: ScheduleItem) => void
  onClose: () => void
}) {
  const initial = useMemo(() => localDateParts(props.item.startTime), [props.item.startTime])
  const isLive = props.item.itemType === 'LIVE'
  const hasProgramFields = ['PROGRAM', 'JINGLE', 'FILLER'].includes(props.item.itemType)
  // Un direct porte un `bundleId` même seul, mais ce n'est pas une partie : son
  // bloc se rallonge librement, l'opérateur décidant de la longueur de son
  // antenne. Seul un programme multipartie tient sa durée du catalogue.
  // Miroir de `bundleConstrainsGridEdit` (`backend/src/lib/bundle-grid-rules.ts`).
  const isBundlePart = Boolean(props.item.bundleId) && props.item.itemType !== 'LIVE'
  const canChooseProgram = hasProgramFields && !isBundlePart
  const [date, setDate] = useState(initial.date)
  const [time, setTime] = useState(initial.time)
  const [durationSec, setDurationSec] = useState(props.item.durationSec)
  const [programId, setProgramId] = useState(props.item.programId ?? '')
  const [mediaAssetId, setMediaAssetId] = useState(props.item.mediaAssetId ?? '')
  const [notes, setNotes] = useState(props.item.notes ?? '')
  const [programs, setPrograms] = useState<Program[]>([])
  const [assets, setAssets] = useState<MediaAsset[]>([])
  // Programme dont `assets` est la liste : un fichier ne part qu'avec lui
  // (`lib/itemMediaChoice.ts`).
  const [loadedMedia, setLoadedMedia] = useState<LoadedProgramMedia | null>(null)
  const [logoProfiles, setLogoProfiles] = useState<LogoProfile[]>([])
  const [liveStartMode, setLiveStartMode] = useState<LiveStartMode>(props.item.liveStartMode ?? 'AUTO')
  const [liveEndMode, setLiveEndMode] = useState<LiveEndMode>(props.item.liveEndMode ?? 'AUTO')
  const [liveHoldClipId, setLiveHoldClipId] = useState(props.item.liveHoldClipId ?? '')
  const [liveSourceId, setLiveSourceId] = useState(props.item.liveSourceId ?? '')
  const [interludeClips, setInterludeClips] = useState<InterludeClip[]>([])
  const [liveSources, setLiveSources] = useState<LiveSource[]>([])
  const [logoProfileId, setLogoProfileId] = useState(() => {
    const overrides = props.item.overrides ?? {}
    if (typeof overrides.logoProfileId === 'string') return overrides.logoProfileId
    // Une fiche marquée « Aucun logo » ne doit pas se retrouver rallumée par la
    // simple ouverture de cette boîte : on retombe sur « Logo du programme ».
    if (props.item.program?.noLogo) return ''
    return props.item.program?.logoProfileId ?? ''
  })
  // Surcharges EPG de cette diffusion : le titre et le sous-titre publiés au
  // partenaire, quand ce que déduit le catalogue (série, épisode, partie) ne dit
  // pas ce que l'antenne annonce ce jour-là. Vides, l'export reste déduit.
  const [epgTitle, setEpgTitle] = useState(() => overrideText(props.item.overrides, 'epgTitle'))
  const [epgSubtitle, setEpgSubtitle] = useState(() => overrideText(props.item.overrides, 'epgSubtitle'))
  const [liveForm, setLiveForm] = useState(() => initLiveForm(props.item))
  // Déroulé estimé du bloc de direct : une annotation, enregistrée par sa propre
  // route une fois la nouvelle durée du bloc écrite (`lib/livePartPlan.ts`).
  const [liveParts, setLiveParts] = useState<LivePartDraft[]>(() => livePartDrafts(props.item))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Le déroulé estimé se cale sur ce que l'opérateur est en train de saisir, pas
  // sur l'horaire déjà enregistré : déplacer le bloc doit déplacer ses parties
  // sous les yeux, avant même d'enregistrer.
  const blockStartMs = useMemo(() => {
    const start = instantAtBroadcastClock(date, time)
    return Number.isNaN(start.getTime()) ? new Date(props.item.startTime).getTime() : start.getTime()
  }, [date, time, props.item.startTime])
  const today = useMemo(() => todayInputValue(), [])
  const isPastDate = Boolean(date && date < today)
  // Mode de départ changé à moins de 2 min du départ (`lib/liveStartModeChange.ts`).
  const [nowMs, setNowMs] = useState(() => getServerNow().getTime())
  useEffect(() => {
    if (!isLive) return
    const timer = setInterval(() => setNowMs(getServerNow().getTime()), 5_000)
    return () => clearInterval(timer)
  }, [isLive])
  const riskyStartModeChange = isLive && isRiskyLiveStartModeChange({
    itemType:     props.item.itemType,
    status:       props.item.status,
    previousMode: props.item.liveStartMode,
    nextMode:     liveStartMode,
    startMs:      blockStartMs,
    nowMs,
  })

  useEffect(() => {
    if (!hasProgramFields) return
    Promise.all([
      programsApi.list({
        channelId: props.channelId,
        catalog: props.item.itemType === 'JINGLE' ? 'jingles' : 'programs',
        limit: 100,
      }),
      logoProfilesApi.list(props.channelId),
    ])
      .then(([result, profiles]) => {
        setPrograms(props.item.itemType === 'JINGLE'
          ? result.data.filter((program: Program) => program.durationSec === props.item.durationSec)
          : result.data)
        setLogoProfiles(profiles)
      })
      .catch(() => {
        setPrograms([])
        setLogoProfiles([])
      })
  }, [hasProgramFields, props.channelId, props.item.durationSec, props.item.itemType])

  useEffect(() => {
    if (!isLive) return
    interludeApi.list(props.channelId)
      .then(clips => setInterludeClips(clips.filter(clip => clip.active && Boolean(clip.filePath))))
      .catch(() => setInterludeClips([]))
    logoProfilesApi.list(props.channelId)
      .then(setLogoProfiles)
      .catch(() => setLogoProfiles([]))
    liveSourcesApi.list(props.channelId)
      .then(setLiveSources)
      .catch(() => setLiveSources([]))
  }, [props.channelId, isLive])

  useEffect(() => {
    if (!programId || !hasProgramFields) {
      setAssets([])
      setLoadedMedia(null)
      return
    }
    // Une réponse arrivée après un nouveau changement de programme est ignorée.
    let cancelled = false
    programsApi.listMedia(programId)
      .then(list => {
        if (cancelled) return
        setAssets(list)
        setLoadedMedia({ programId, assetIds: list.map(asset => asset.id) })
        setMediaAssetId(current => list.some(asset => asset.id === current) ? current : list.find(asset => !asset.isProxy)?.id ?? '')
      })
      .catch(() => {
        if (cancelled) return
        setAssets([])
        setLoadedMedia(null)
      })
    return () => { cancelled = true }
  }, [hasProgramFields, programId])

  const save = async () => {
    const start = instantAtBroadcastClock(date, time)
    if (!date || !time || Number.isNaN(start.getTime())) return setError('Date ou heure invalide.')
    if (!Number.isInteger(durationSec) || durationSec <= 0) return setError('La durée doit être supérieure à zéro.')
    if (canChooseProgram && !programId) return setError('Sélectionnez un programme.')
    if (isLive && !liveForm.title.trim()) return setError('Le titre du direct est requis.')

    const baseOverrides = (props.item.overrides && typeof props.item.overrides === 'object' && !Array.isArray(props.item.overrides))
      ? (props.item.overrides as Record<string, unknown>)
      : {}
    // Un direct porte ses métadonnées EPG dans ses overrides (et les propage à
    // sa fiche de rediffusion côté serveur). Un programme n'expose ici que le
    // titre/sous-titre EPG de la diffusion.
    const overridesPayload = isLive
      ? {
          ...baseOverrides,
          title:         liveForm.title.trim(),
          titleShort:    liveForm.titleShort.trim() || null,
          summary:       liveForm.summary.trim() || null,
          genre:         liveForm.genre.trim() || null,
          tags:          liveForm.tags.split(',').map(tag => tag.trim()).filter(Boolean),
          director:      liveForm.director.trim() || null,
          presenter:     liveForm.presenter.trim() || null,
          production:    liveForm.production.trim() || null,
          year:          liveForm.year ? Number(liveForm.year) : null,
          isNew:         liveForm.isNew,
          logoProfileId: liveForm.logoProfileId || null,
          epgSubtitle:   liveForm.epgSubtitle.trim() || null,
          epgFederation: liveForm.epgFederation.trim() || null,
          epgThemeId:    liveForm.epgThemeId ? Number(liveForm.epgThemeId) : null,
          epgShowId:     liveForm.epgShowId ? Number(liveForm.epgShowId) : null,
          createReplayProgram: Boolean(props.item.programId),
        }
      : {
          ...baseOverrides,
          logoProfileId: logoProfileId || null,
          epgTitle:    epgTitle.trim()    || null,
          epgSubtitle: epgSubtitle.trim() || null,
        }

    // Jamais le fichier d'un autre programme que celui sélectionné.
    const mediaToSend = mediaAssetIdToSend({ programId, mediaAssetId, loaded: loadedMedia })

    setBusy(true)
    setError(null)
    try {
      if (riskyStartModeChange && !window.confirm(`${RISKY_LIVE_START_MODE_MESSAGE}\n\nEnregistrer quand même ?`)) {
        return
      }
      // L'édition peut déplacer l'élément d'un jour à l'autre : la fenêtre
      // photographiée couvre la journée de départ comme celle d'arrivée, sans
      // quoi l'annulation ne verrait qu'un des deux bouts du déplacement.
      const previousDay = broadcastDayKey(props.item.startTime)
      const nextDay     = broadcastDayKey(start)
      const updated = await recordGridChange({
        label:   `modification de ${props.item.program?.title ?? 'un élément'}`,
        gridIds: [props.item.gridId],
        date:    previousDay <= nextDay ? previousDay : nextDay,
        until:   previousDay <= nextDay ? nextDay : previousDay,
      }, () => itemsApi.update(props.item.id, {
        startTime: start.toISOString(),
        ...(!isBundlePart ? { durationSec } : {}),
        notes: notes.trim(),
        ...(isLive ? {
          liveSourceId: liveSourceId || null,
          liveStartMode,
          liveEndMode,
          liveHoldClipId: liveStartMode === 'MANUAL' ? liveHoldClipId || null : null,
        } : {}),
        // `null` et non `''` : l'export ne doit pas publier un champ vide, il
        // doit revenir à la déduction du catalogue.
        overrides: overridesPayload,
        ...(canChooseProgram ? {
          programId,
          ...(mediaToSend ? { mediaAssetId: mediaToSend } : {}),
        } : isBundlePart && mediaToSend ? { mediaAssetId: mediaToSend } : {}),
      }))
      // Le déroulé part après l'élément : ses bornes sont celles du bloc qui
      // vient d'être écrit, et le serveur refuse une estimation plus longue que
      // lui. Un direct sans déroulé n'appelle la route que s'il en avait un.
      const planned = isLive && (liveParts.length > 0 || (props.item.liveParts?.length ?? 0) > 0)
        ? await itemsApi.setLiveParts(props.item.id, liveParts.map(part => ({
            title:         part.title,
            durationSec:   part.durationSec,
            breakAfterSec: part.breakAfterSec,
          })))
        : null
      // Seul le déroulé est repris de cette seconde réponse : elle ne porte pas
      // les placements publicitaires ni l'as-run que la vue attend de `update`.
      props.onSaved(planned ? { ...updated, liveParts: planned.liveParts } : updated)
      props.onClose()
    } catch (cause: any) {
      setError(cause?.message ?? 'Impossible de modifier cet élément.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <ModalShell title="Modifier la diffusion" onClose={props.onClose} size="medium" className="agenda-dialog">
      <div className="agenda-dialog__subtitle">{props.item.program?.title ?? props.item.liveSource?.name ?? props.item.interludeClip?.name ?? (props.item.itemType === 'AD_BREAK' ? 'Bloc pubs' : props.item.itemType === 'INFOSERVICE' ? 'Infoservice' : props.item.itemType)}</div>
      <div className="tp-modal-form agenda-dialog__form">
        {isPastDate && (
          <div className="tp-edit-date-warning" role="status">
            <AlertTriangle size={15} aria-hidden="true" />
            <span>Cette diffusion est encore placée sur un jour passé.</span>
            <button className="tp-button tp-button--warning" onClick={() => setDate(today)}>Aujourd’hui</button>
          </div>
        )}
        <div className="agenda-dialog__two-columns">
          <Field label="Date de diffusion" icon={<CalendarDays size={12} aria-hidden="true" />}><input className="tp-input" type="date" value={date} onChange={event => setDate(event.target.value)} /></Field>
          <Field label="Heure de début" icon={<Clock3 size={12} aria-hidden="true" />}><input className="tp-input" type="time" step="1" value={time} onChange={event => setTime(event.target.value)} /></Field>
        </div>
        {isBundlePart && (
          <div className="agenda-dialog__bundle-note" role="note">
            Cette partie peut être déplacée indépendamment pour agrandir la coupure. Sa durée et son programme restent ceux de la fiche multipartie.
          </div>
        )}
        <Field label={`Durée (secondes) · ${formatDuration(durationSec)}`}><input className="tp-input" type="number" min="1" value={durationSec} disabled={isBundlePart} onChange={event => setDurationSec(Number(event.target.value))} /></Field>
        {isLive && durationSec !== props.item.durationSec && (
          // Le serveur ne recopie jamais la durée d'un bloc de direct sur sa
          // fiche (`ScheduleService.update`) : la fiche décrit la rediffusion.
          <div className="agenda-dialog__bundle-note" role="note">
            Ne change que ce direct : sa fiche et ses rediffusions gardent leur durée. Ce qui suit dans la grille ne bouge pas.
          </div>
        )}
        {props.item.itemType === 'LIVE' && (
          <div className="agenda-dialog__two-columns">
            <Field label="Source">
              <select className="tp-input" value={liveSourceId} onChange={event => setLiveSourceId(event.target.value)}>
                <option value="">Aucune source liée</option>
                {liveSources.filter(source => source.active).map(source => <option key={source.id} value={source.id}>{source.name} ({source.sourceType})</option>)}
              </select>
            </Field>
            <Field label="Lancement du direct">
              <select className="tp-input" value={liveStartMode} onChange={event => setLiveStartMode(event.target.value as LiveStartMode)}>
                <option value="AUTO">Automatique à l'heure prévue</option>
                <option value="MANUAL">Manuel par l'opérateur</option>
              </select>
            </Field>
            {riskyStartModeChange && (
              <div className="tp-edit-date-warning" role="alert">
                <AlertTriangle size={15} aria-hidden="true" />
                <span>{RISKY_LIVE_START_MODE_MESSAGE}</span>
              </div>
            )}
            {liveStartMode === 'MANUAL' && (
              <Field label="Vidéo d'attente">
                <select className="tp-input" value={liveHoldClipId} onChange={event => setLiveHoldClipId(event.target.value)}>
                  <option value="">Interlude par défaut de la chaîne</option>
                  {interludeClips.map(clip => <option key={clip.id} value={clip.id}>{clip.name}</option>)}
                </select>
              </Field>
            )}
            <Field label="Fin du direct">
              <select className="tp-input" value={liveEndMode} onChange={event => setLiveEndMode(event.target.value as LiveEndMode)}>
                <option value="AUTO">Automatique à l'heure prévue</option>
                <option value="MANUAL">Manuel par l'opérateur</option>
              </select>
            </Field>
          </div>
        )}
        {isLive && (
          <LivePartPlanEditor
            blockStartMs={blockStartMs}
            blockDurationSec={durationSec}
            parts={liveParts}
            onChange={setLiveParts}
            disabled={busy}
          />
        )}
        {isLive && (
          <>
            <Field label="Titre du direct">
              <input className="tp-input" value={liveForm.title} onChange={event => setLiveForm(form => ({ ...form, title: event.target.value }))} />
            </Field>
            <div className="agenda-dialog__two-columns">
              <Field label="Titre court">
                <input className="tp-input" value={liveForm.titleShort} onChange={event => setLiveForm(form => ({ ...form, titleShort: event.target.value }))} />
              </Field>
              <Field label="Genre">
                <input className="tp-input" value={liveForm.genre} onChange={event => setLiveForm(form => ({ ...form, genre: event.target.value }))} />
              </Field>
            </div>
            <Field label="Sous-titre EPG">
              <input className="tp-input" value={liveForm.epgSubtitle} placeholder="Édition du jour, invité, thème…" onChange={event => setLiveForm(form => ({ ...form, epgSubtitle: event.target.value }))} />
            </Field>
            <Field label="Résumé">
              <textarea className="tp-input agenda-dialog__textarea" value={liveForm.summary} onChange={event => setLiveForm(form => ({ ...form, summary: event.target.value }))} />
            </Field>
            <Field label="Tags (séparés par des virgules)">
              <input className="tp-input" value={liveForm.tags} onChange={event => setLiveForm(form => ({ ...form, tags: event.target.value }))} />
            </Field>
            <div className="agenda-dialog__two-columns">
              <Field label="Réalisateur">
                <input className="tp-input" value={liveForm.director} onChange={event => setLiveForm(form => ({ ...form, director: event.target.value }))} />
              </Field>
              <Field label="Présentateur">
                <input className="tp-input" value={liveForm.presenter} onChange={event => setLiveForm(form => ({ ...form, presenter: event.target.value }))} />
              </Field>
            </div>
            <div className="agenda-dialog__two-columns">
              <Field label="Production">
                <input className="tp-input" value={liveForm.production} onChange={event => setLiveForm(form => ({ ...form, production: event.target.value }))} />
              </Field>
              <Field label="Année">
                <input className="tp-input" type="number" min={1900} max={2100} value={liveForm.year} onChange={event => setLiveForm(form => ({ ...form, year: event.target.value }))} />
              </Field>
            </div>
            <Field label="Logo antenne">
              <select className="tp-input" value={liveForm.logoProfileId} onChange={event => setLiveForm(form => ({ ...form, logoProfileId: event.target.value }))}>
                <option value="">Logo du programme</option>
                {logoProfiles.filter(profile => profile.active).map(profile => <option key={profile.id} value={profile.id}>{profile.name}</option>)}
              </select>
            </Field>
            <label className="agenda-dialog__checkbox">
              <input type="checkbox" checked={liveForm.isNew} onChange={event => setLiveForm(form => ({ ...form, isNew: event.target.checked }))} />
              Inédit
            </label>
            <div className="agenda-dialog__two-columns">
              <Field label="Fédération EPG">
                <input className="tp-input" value={liveForm.epgFederation} onChange={event => setLiveForm(form => ({ ...form, epgFederation: event.target.value }))} />
              </Field>
              <Field label="Thème EPG (Plurimedia)">
                <input className="tp-input" type="number" min={0} value={liveForm.epgThemeId} onChange={event => setLiveForm(form => ({ ...form, epgThemeId: event.target.value }))} />
              </Field>
            </div>
            <Field label="Émission EPG (Plurimedia)">
              <input className="tp-input" type="number" min={0} value={liveForm.epgShowId} onChange={event => setLiveForm(form => ({ ...form, epgShowId: event.target.value }))} />
            </Field>
          </>
        )}
        {hasProgramFields && <>
          <Field label="Programme">
            <select className="tp-input" value={programId} disabled={isBundlePart} onChange={event => {
              const next = event.target.value
              setProgramId(next)
              // Le fichier choisi était celui de l'ancien programme.
              setMediaAssetId('')
              setAssets([])
              setLoadedMedia(null)
              const program = programs.find(value => value.id === next)
              if (program) setDurationSec(program.durationSec)
            }}>
              <option value="">Sélectionner…</option>
              {props.item.programId && !programs.some(program => program.id === props.item.programId) && (
                <option value={props.item.programId}>{props.item.program?.title ?? 'Programme actuel'}</option>
              )}
              {programs.map(program => <option key={program.id} value={program.id}>{program.title}</option>)}
            </select>
          </Field>
          <Field label="Logo antenne">
            <select className="tp-input" value={logoProfileId} onChange={event => setLogoProfileId(event.target.value)}>
              <option value="">Logo du programme</option>
              {logoProfiles.filter(profile => profile.active).map(profile => <option key={profile.id} value={profile.id}>{profile.name}</option>)}
              <option value={NO_LOGO_VALUE}>Aucun logo</option>
            </select>
          </Field>
          <Field label="Média">
            <select className="tp-input" value={mediaAssetId} onChange={event => setMediaAssetId(event.target.value)}>
              <option value="">
                {props.item.mediaAssetId
                  ? 'Conserver le média actuellement associé'
                  : 'Aucun média — associer plus tard'}
              </option>
              {assets.filter(asset => !asset.isProxy).map(asset => <option key={asset.id} value={asset.id}>{asset.fileName}</option>)}
            </select>
          </Field>
        </>}
        {props.item.itemType === 'PROGRAM' && <>
          <Field label="Titre EPG de cette diffusion">
            <input className="tp-input" maxLength={200} placeholder="Vide : titre déduit du catalogue" value={epgTitle} onChange={event => setEpgTitle(event.target.value)} />
          </Field>
          <Field label="Sous-titre EPG de cette diffusion">
            <input className="tp-input" maxLength={200} placeholder="Vide : épisode ou numéro de partie" value={epgSubtitle} onChange={event => setEpgSubtitle(event.target.value)} />
          </Field>
        </>}
        <Field label="Notes"><textarea className="tp-input agenda-dialog__textarea" maxLength={500} value={notes} onChange={event => setNotes(event.target.value)} /></Field>
        {error && <div className="agenda-dialog__error" role="alert">{error}</div>}
        <div className="tp-modal-actions">
          <button className="tp-button" onClick={props.onClose}>Annuler</button>
          <button className="tp-button tp-button--primary" disabled={busy} data-state={busy ? 'loading' : undefined} onClick={save}>{busy ? 'Enregistrement…' : 'Enregistrer les modifications'}</button>
        </div>
      </div>
    </ModalShell>
  )
})

const Field = memo(function Field(props: { label: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return <label className="agenda-dialog__field"><span className="agenda-dialog__label">{props.icon}{props.label}</span>{props.children}</label>
})
